import React, { createContext, useContext, useState, useEffect } from 'react';

const BlogContext = createContext();

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};

export const BlogProvider = ({ children }) => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);

  // Load blog posts from localStorage on mount
  useEffect(() => {
    const savedPosts = localStorage.getItem('blogPosts');
    if (savedPosts) {
      setBlogPosts(JSON.parse(savedPosts));
    } else {
      // Add some sample blog posts for demonstration
      const samplePosts = [
        {
          id: '1',
          title: 'The Ultimate Guide to Mobile Car Wash Services in UAE',
          content: `<h2>Why Choose Mobile Car Wash?</h2>
<p>In today's fast-paced world, convenience is key. Mobile car wash services bring professional cleaning directly to your doorstep, saving you time and eliminating the hassle of traditional car washes.</p>

<h2>Benefits of Quick Way Mobile Car Wash</h2>
<ul>
<li><strong>Convenience:</strong> We come to you at home, office, or parking</li>
<li><strong>Clear prices:</strong> Packages from AED 30, with every inclusion listed</li>
<li><strong>Easy booking:</strong> Book on WhatsApp, on our website or in the Android app</li>
<li><strong>Abu Dhabi and Dubai:</strong> Available across both cities</li>
</ul>`,
          featuredImage: '/blog/mobile-car-wash-guide.jpg',
          seoTitle: 'Mobile Car Wash Guide UAE - Quick Way Services',
          seoDescription: 'Complete guide to mobile car wash services in UAE. Learn about benefits, pricing, and how to book professional car cleaning at your location.',
          seoKeywords: 'mobile car wash UAE, car wash Dubai, car wash Abu Dhabi, doorstep car wash',
          published: true,
          createdAt: new Date('2024-01-15').toISOString(),
          updatedAt: new Date('2024-01-15').toISOString(),
          slug: 'ultimate-guide-mobile-car-wash-uae'
        },
        {
          id: '2',
          title: 'How Often Should You Wash Your Car in the UAE?',
          content: `<h2>Dust, sand and sun</h2>
<p>Dust and sand settle on cars quickly in the UAE, especially after windy days. Grit left on paint can cause fine scratches when the car is wiped, and strong sun can bake dirt onto the surface.</p>

<h2>A simple routine</h2>
<ul>
<li><strong>Weekly:</strong> An exterior wash for cars parked outdoors</li>
<li><strong>Every few weeks:</strong> An interior vacuum and wipe-down</li>
<li><strong>Every few months:</strong> A detail with clay bar and wax to protect the paint</li>
</ul>`,
          featuredImage: '/blog/eco-friendly-car-wash.jpg',
          seoTitle: 'How Often to Wash Your Car in the UAE',
          seoDescription: 'A simple car washing routine for UAE weather: how often to wash the exterior, clean the interior and protect your paint.',
          seoKeywords: 'how often wash car UAE, car care routine',
          published: true,
          createdAt: new Date('2024-01-20').toISOString(),
          updatedAt: new Date('2024-01-20').toISOString(),
          slug: 'eco-friendly-car-cleaning-guide'
        }
      ];
      setBlogPosts(samplePosts);
      localStorage.setItem('blogPosts', JSON.stringify(samplePosts));
    }
  }, []);

  // Save to localStorage whenever blogPosts changes
  useEffect(() => {
    if (blogPosts.length > 0) {
      localStorage.setItem('blogPosts', JSON.stringify(blogPosts));
    }
  }, [blogPosts]);

  const generateSlug = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const addBlogPost = (post) => {
    const newPost = {
      ...post,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      slug: post.slug && post.slug.trim() ? post.slug : generateSlug(post.title)
    };
    setBlogPosts(prev => [newPost, ...prev]);
    return newPost;
  };

  const updateBlogPost = (id, updates) => {
    setBlogPosts(prev => prev.map(post =>
      post.id === id
        ? {
            ...post,
            ...updates,
            slug:
              updates.slug && updates.slug.trim()
                ? generateSlug(updates.slug)
                : post.slug || generateSlug(post.title),
            updatedAt: new Date().toISOString()
          }
        : post
    ));
  };

  const deleteBlogPost = (id) => {
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      setBlogPosts(prev => prev.filter(post => post.id !== id));
      return true;
    }
    return false;
  };

  const getBlogPostBySlug = (slug) => {
    return blogPosts.find(post => post.slug === slug);
  };

  const getPublishedPosts = () => {
    return blogPosts.filter(post => post.published);
  };

  const value = {
    blogPosts,
    currentPost,
    setCurrentPost,
    addBlogPost,
    updateBlogPost,
    deleteBlogPost,
    getBlogPostBySlug,
    getPublishedPosts
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
};
